import * as React from "react";
import {css} from "emotion";
import {BaseComponent, Dialog_Builder} from "@intuitionrobotics/thunderstorm/frontend";
import {ActivationsHistoryInterface, authType, HistoryActions} from "@app/ir-q-app-common/types/activation";
import {ActivationsModule} from "@modules/ActivationsModule";
import {SimpleLoader} from "@components/SimpleLoader";
import {closeButton} from "@components/AppDialog";

type Props = { unitId: string }

type State = {
    loading: boolean
    rows: ActivationsHistoryInterface[]
}

const wrapper = css({
    minWidth: 780,
    maxWidth: "92vw",
    maxHeight: "70vh",
    overflow: "hidden",
    display: "flex",
    flexDirection: "column",
    background: "white"
});

const headerStyle = css({
    padding: "20px 24px 14px 24px",
    borderBottom: "1px solid #ececec"
});

const headerTitle = css({
    fontSize: 18,
    fontWeight: 600,
    color: "#1a1a1a",
    lineHeight: 1.2
});

const headerSubtitle = css({
    marginTop: 4,
    fontSize: 13,
    color: "#777"
});

const scrollArea = css({
    overflow: "auto",
    padding: "8px 12px 16px 12px"
});

const tableStyle = css({
    width: "100%",
    borderCollapse: "separate",
    borderSpacing: 0,
    fontSize: 13,
    color: "#222"
});

const thStyle = css({
    textAlign: "left",
    padding: "10px 12px",
    background: "#fafafa",
    color: "#555",
    fontWeight: 600,
    fontSize: 12,
    letterSpacing: 0.3,
    textTransform: "uppercase",
    borderBottom: "1px solid #e6e6e6",
    position: "sticky",
    top: 0,
    whiteSpace: "nowrap",
    zIndex: 1
});

const tdStyle = css({
    padding: "9px 12px",
    borderBottom: "1px solid #f1f1f1",
    whiteSpace: "nowrap",
    verticalAlign: "middle"
});

const rowStyle = css({
    "&:hover td": {background: "#f8fafd"}
});

const dateStyle = css({
    fontVariantNumeric: "tabular-nums",
    color: "#333"
});

const monoStyle = css({
    fontFamily: "Menlo, Consolas, monospace",
    fontSize: 12,
    color: "#444"
});

const mutedStyle = css({color: "#888"});

const empty = css({
    padding: "40px 12px",
    textAlign: "center",
    color: "#666"
});

const actionChip = (action?: string) => {
    const base = {
        display: "inline-block",
        padding: "2px 8px",
        borderRadius: 10,
        fontSize: 11,
        fontWeight: 600,
        letterSpacing: 0.4,
        textTransform: "uppercase" as const
    };
    switch (action) {
        case HistoryActions.CREATED:
            return css({...base, background: "#e6f4ea", color: "#1e7e34"});
        case HistoryActions.UPDATED:
            return css({...base, background: "#e7f0fd", color: "#1559b3"});
        case HistoryActions.DELETED:
            return css({...base, background: "#fdecea", color: "#a8261b"});
        default:
            return css({...base, background: "#eee", color: "#555"});
    }
};

const formatDate = (value?: number | string | Date | null) => {
    if (value === undefined || value === null || value === "")
        return "";
    const d = value instanceof Date ? value : new Date(value);
    if (isNaN(d.getTime()))
        return String(value);
    const pad = (n: number) => String(n).padStart(2, "0");
    return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())} ${pad(d.getHours())}:${pad(d.getMinutes())}:${pad(d.getSeconds())}`;
};

const stripDomain = (email?: string) =>
    (email && typeof email === "string") ? email.split("@")[0] : (email || "");

const toMillis = (value?: number | string | Date | null): number => {
    if (value === undefined || value === null || value === "")
        return 0;
    if (typeof value === "number")
        return value;
    if (value instanceof Date)
        return value.getTime();
    const t = new Date(value).getTime();
    return isNaN(t) ? 0 : t;
};

export class Dialog_ActivationHistory
    extends BaseComponent<Props, State> {

    constructor(props: Props) {
        super(props);
        this.state = {loading: true, rows: []};
    }

    componentDidMount() {
        ActivationsModule.fetchActivationHistory(this.props.unitId)
            .then(rows => this.setState({
                loading: false,
                rows: (rows || []).slice().sort((a, b) => toMillis(b.modification_date as any) - toMillis(a.modification_date as any))
            }))
            .catch(() => this.setState({loading: false, rows: []}));
    }

    private renderHeader(rowCount?: number) {
        const subtitle = typeof rowCount === "number"
            ? (rowCount === 1 ? "1 record" : `${rowCount} records`)
            : "Loading…";
        return <div className={headerStyle}>
            <div className={headerTitle}>Activation history</div>
            <div className={headerSubtitle}>Unit {this.props.unitId} · {subtitle}</div>
        </div>;
    }

    render() {
        const {loading, rows} = this.state;

        if (loading)
            return <div className={wrapper}>
                {this.renderHeader()}
                <div className={scrollArea} style={{minHeight: 180, display: "flex", alignItems: "center", justifyContent: "center"}}>
                    <SimpleLoader/>
                </div>
            </div>;

        if (!rows.length)
            return <div className={wrapper}>
                {this.renderHeader(0)}
                <div className={scrollArea}><div className={empty}>No activation history for this unit.</div></div>
            </div>;

        return <div className={wrapper}>
            {this.renderHeader(rows.length)}
            <div className={scrollArea}>
                <table className={tableStyle}>
                    <thead>
                    <tr>
                        <th className={thStyle}>Date</th>
                        <th className={thStyle}>Action</th>
                        <th className={thStyle}>By</th>
                        <th className={thStyle}>Auth type</th>
                        <th className={thStyle}>Activated</th>
                        <th className={thStyle}>SOM</th>
                        <th className={thStyle}>Tablet</th>
                        <th className={thStyle}>Activation&nbsp;ID</th>
                    </tr>
                    </thead>
                    <tbody>
                    {rows.map((r, i) => (
                        <tr key={`${r.activation_id}_${String(r.modification_date)}_${i}`} className={rowStyle}>
                            <td className={`${tdStyle} ${dateStyle}`}>{formatDate(r.modification_date as any)}</td>
                            <td className={tdStyle}>
                                <span className={actionChip(r.description)}>{r.description}</span>
                            </td>
                            <td className={tdStyle}>{stripDomain(r.blame) || <span className={mutedStyle}>—</span>}</td>
                            <td className={tdStyle}>{authType[r.auth_type] || r.auth_type}</td>
                            <td className={tdStyle}>{r.activated ? "Yes" : <span className={mutedStyle}>No</span>}</td>
                            <td className={`${tdStyle} ${monoStyle}`}>{r.som_device_id || <span className={mutedStyle}>—</span>}</td>
                            <td className={`${tdStyle} ${monoStyle}`}>{r.tablet_device_id || <span className={mutedStyle}>—</span>}</td>
                            <td className={`${tdStyle} ${monoStyle}`}>{r.activation_id}</td>
                        </tr>
                    ))}
                    </tbody>
                </table>
            </div>
        </div>;
    }
}

export const showActivationHistoryDialog = (unitId: string) =>
    new Dialog_Builder(<Dialog_ActivationHistory unitId={unitId}/>)
        .setAllowIndirectClosing(true)
        .setButtons(closeButton("Close"))
        .show();
